import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Copy } from 'lucide-react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import {
  formatReleaseNoteAsset,
  parseReleaseNoteAsset,
  releaseNoteAssetImgTag,
  uploadReleaseNoteAsset,
} from '@/services/releaseNotes/releaseNotesApi'
import { copyToClipboard } from '@/utils/clipboard'
import { formatBytes } from '../utils/format'
import { FileDropzone } from './FileDropzone'

/** The image types UII accepts for a release note. */
const UPLOAD_ACCEPT = '.webp,.jpg,.png,.jpeg,.gif,.svg'

interface ReleaseNoteAssetsProps {
  /** The note's assets, in UII's `{}`-separated spelling. */
  value: string[]
  /** Omit to show the assets read-only. */
  onChange?: (value: string[]) => void
}

/**
 * Images uploaded to the CDN for a release note, each with the URL and `<img>`
 * tag to paste into its content.
 */
export function ReleaseNoteAssets({ value, onChange }: ReleaseNoteAssetsProps) {
  const { t } = useTranslation()
  const [uploading, setUploading] = useState(false)
  const assets = value.map(parseReleaseNoteAsset)

  const upload = async (file: File) => {
    if (!onChange) return
    setUploading(true)
    try {
      const asset = await uploadReleaseNoteAsset(file)
      onChange([...value, formatReleaseNoteAsset(asset)])
    } catch (e) {
      toast.error(t('releaseNotes.uploadFailed'), {
        description: e instanceof Error ? e.message : undefined,
        duration: 5000,
      })
    } finally {
      setUploading(false)
    }
  }

  return (
    <div className="flex flex-col gap-3">
      {assets.length === 0 && !onChange && (
        <p className="text-xs text-muted-foreground">{t('releaseNotes.noAssets')}</p>
      )}
      {assets.map((asset, i) => (
        <div key={asset.id || i} className="flex flex-col gap-3 rounded-md border p-3 sm:flex-row">
          <img
            src={asset.cdnId}
            alt={asset.fileName}
            className="max-h-32 max-w-40 self-start rounded object-contain"
          />
          <div className="flex min-w-0 flex-1 flex-col gap-1 text-xs">
            <span className="font-semibold break-all">{asset.fileName}</span>
            <span className="text-(--ad-text-muted)">
              {asset.width}×{asset.height} · {asset.mimeType} · {formatBytes(asset.fileSizeBytes)}
            </span>
            <code className="font-mono break-all text-(--ad-text-faint)">{asset.cdnId}</code>
            <div className="mt-1 flex flex-wrap gap-2">
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => copyToClipboard(asset.cdnId)}
              >
                <Copy />
                {t('releaseNotes.copyImageUrl')}
              </Button>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => copyToClipboard(releaseNoteAssetImgTag(asset))}
              >
                <Copy />
                {t('releaseNotes.copyImageTag')}
              </Button>
            </div>
          </div>
        </div>
      ))}
      {onChange && (
        <FileDropzone
          accept={UPLOAD_ACCEPT}
          onFile={upload}
          busy={uploading}
          label={t('releaseNotes.uploadAsset')}
        />
      )}
    </div>
  )
}
